import React, { useState, useEffect, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import {
 getAuth,
 onAuthStateChanged,
 signOut
} from 'firebase/auth';
import {
 getFirestore,
 collection,
 doc,
 addDoc,
 setDoc,
 getDoc,
 getDocs,
 query,
 orderBy,
 onSnapshot,
 serverTimestamp,
 where
} from 'firebase/firestore';
import { Send, Image, ArrowRightToLine, LogOut, User, Users, Search, X, ArrowLeft, Menu, MoreVertical, Heart, Coffee } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import Header from './Header';
import Footer from './Footer';

// Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// EmailJS configuration
const emailjsConfig = {
  serviceId: 'service_mtisp1n',
  templateId: 'template_av6b6tk',
  userId: '2WEUoRhkVGsmiYGCV'
};

// Cloudinary configuration
const cloudinaryConfig = {
  cloudName: 'dryrxwnej',
  uploadPreset: 'chat-uploads',
  apiUrl: 'https://api.cloudinary.com/v1_1/dryrxwnej/image/upload'
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// Initialize EmailJS
emailjs.init(emailjsConfig.userId);

// Notification Component
function NotificationBanner({ message, onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000); // Auto-hide after 5 seconds
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-blue-600 text-white p-3 shadow-lg animate-slide-down">
      <div className="flex items-center justify-between max-w-4xl mx-auto">
        <div className="flex items-center">
          <div className="w-2 h-2 bg-blue-300 rounded-full mr-3 animate-pulse"></div>
          <span className="font-medium">New message: {message}</span>
        </div>
        <button
          onClick={onClose}
          className="text-blue-200 hover:text-white transition-colors"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}

// Main App Component
function Chat() {
 const [user, setUser] = useState(null);
 const [loading, setLoading] = useState(true);
 const navigate = useNavigate();

 useEffect(() => {
   const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
     if (currentUser) {
       // Set user immediately with basic info to stop loading
       const basicUser = {
         ...currentUser,
         uid: currentUser.uid,
         displayName: currentUser.displayName || currentUser.email.split('@')[0]
       };
       setUser(basicUser);
       setLoading(false); // Stop loading immediately
       
       // Then fetch additional data in background
       try {
         const userDocRef = doc(db, "users", currentUser.uid);
         const userDoc = await getDoc(userDocRef);
         
         if (userDoc.exists()) {
           const userData = userDoc.data();
           setUser(prev => ({
             ...prev,
             ...userData,
             displayName: userData.username || currentUser.email.split('@')[0]
           }));
           
           // Update online status in background
           setDoc(userDocRef, {
             isOnline: true,
             lastSeen: serverTimestamp()
           }, { merge: true }).catch(console.error);
         } else {
           const username = currentUser.displayName || currentUser.email.split('@')[0];
           
           const userData = {
             uid: currentUser.uid,
             email: currentUser.email,
             username: username,
             isOnline: true,
             lastSeen: serverTimestamp(),
             createdAt: serverTimestamp()
           };
           
           // Create user doc in background
           setDoc(userDocRef, userData).catch(console.error);
           setUser(prev => ({
             ...prev,
             ...userData,
             displayName: username
           }));
         }
       } catch (error) {
         console.error("Error fetching user data:", error);
         // User is still set, so app continues to work
       }
     } else {
       setUser(null);
       setLoading(false);
       navigate('/signup');
     }
   });
   
   return () => unsubscribe();
 }, [navigate]);

 if (loading) {
   return (
     <div className="flex items-center justify-center h-screen bg-stone-50">
       <div className="text-center">
         <div className="w-8 h-8 border-3 border-stone-300 border-t-stone-600 rounded-full animate-spin mb-4 mx-auto"></div>
         <p className="text-stone-600 font-medium">Loading...</p>
       </div>
     </div>
   );
 }

 return (
  <div className="flex flex-col h-screen bg-stone-50">
    <Header user={user} />
    {user ? <ChatApp user={user} /> : null}
  </div>
);
}

// Main Chat Application
function ChatApp({ user }) {
const { userId } = useParams();
 const [allContacts, setAllContacts] = useState([]);
 const [conversations, setConversations] = useState([]);
 const [activeConversations, setActiveConversations] = useState([]);
 const [searchQuery, setSearchQuery] = useState('');
 const [searchResults, setSearchResults] = useState([]);
 const [selectedContact, setSelectedContact] = useState(null);
 const [messages, setMessages] = useState([]);
 const [messageText, setMessageText] = useState('');
 const [imageFile, setImageFile] = useState(null);
 const [imagePreview, setImagePreview] = useState(null);
 const [isLoading, setIsLoading] = useState(false);
 const [uploadProgress, setUploadProgress] = useState(0);
 const [showSidebar, setShowSidebar] = useState(true);
 const [notification, setNotification] = useState(null);
 const fileInputRef = useRef(null);
 const messagesEndRef = useRef(null);
 const audioRef = useRef(null);
 const lastMessageCountRef = useRef(0);

 // Check if current user has search access
 const hasSearchAccess = user?.uid === 'nhm1gYlqiLR3Wfo5rGssZx7dRhI3';

 // Initialize notification sound
 useEffect(() => {
   // Create audio element for notification sound
   audioRef.current = new Audio();
   // Using a data URL for a simple notification beep
   audioRef.current.src = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuN1POScCgGLIHO8tiJNwgZaLvt559NEAxQp+Pwt2MdBjiP1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuN1POScCgGLYDM89qLOwgVZr3n559OEAxPp+Pwt2ocBjiP1/HNeSgFJHfH8N2PQQkUXrPr6qhWFAlGn+DyvmwhBSuN1POScCgGLYPJ89iKNwcYaLrp6J9OEglPpuDwt2McBT2N1O7JeysIJXnE8dtTQAUGSr/v5aZUFw1Ull8Vw/L+r8U2N8nOzv9v3Pz+t1L+1/k8L+X+v2M+1fns/2Dw/8fkf7Tb+T+uY8wO1ff6N8v9t7+nF9u3y8Qz/8U6r+v/2C+1/n/3k/8/8/5Cv3/vGN+1fL/n/5+8/3/AAAE7C+uu3CZZAAElFhZ8dxdNZuGXU1nP3JzKCwFIH/K9tx+QAkRXqzs6aFWF41Gm9/xu2khBSqN1fWWcigGJn7J8tyEOwgVabDn6Z9OFQxOpurwt2IcBjaP1+/JeCgGJHfH8N2QQAoUXrTp66hVFApGn+DyrlcK0DuV/8/2Bw=';
   audioRef.current.volume = 0.3;
   
   return () => {
     if (audioRef.current) {
       audioRef.current = null;
     }
   };
 }, []);

 // Play notification sound
 const playNotificationSound = () => {
   if (audioRef.current) {
     audioRef.current.currentTime = 0;
     audioRef.current.play().catch(err => console.log('Audio play failed:', err));
   }
 };

 // Show notification banner
 const showNotification = (message) => {
   setNotification(message);
   playNotificationSound();
 };

 useEffect(() => {
  if (userId && allContacts.length > 0) {
    const contactToSelect = allContacts.find(contact => contact.id === userId);
    if (contactToSelect && (!selectedContact || selectedContact.id !== userId)) {
      setSelectedContact(contactToSelect);
      if (window.innerWidth < 768) {
        setShowSidebar(false);
      }
    }
  }
}, [userId, allContacts, selectedContact]);

 // Optimized data loading - load in parallel and with better error handling
 useEffect(() => {
   if (!user?.uid) return;

   let usersUnsubscribe = () => {};
   let chatsUnsubscribe = () => {};

   const setupRealtimeListeners = async () => {
     try {
       // Load users with simplified query
       const usersRef = collection(db, "users");
       usersUnsubscribe = onSnapshot(usersRef, (querySnapshot) => {
         const usersList = [];
         querySnapshot.forEach((docSnapshot) => {
           const userData = docSnapshot.data();
           if (userData?.uid && userData.uid !== user.uid) {
             const displayName = userData.username || userData.email?.split('@')[0] || 'User';
             usersList.push({
               id: docSnapshot.id,
               ...userData,
               displayName
             });
           }
         });
         setAllContacts(usersList);
       }, (error) => {
         console.error("Error fetching users:", error);
       });

       // Load conversations with better batching
       const userChatsRef = collection(db, "users", user.uid, "chats");
       chatsUnsubscribe = onSnapshot(userChatsRef, async (querySnapshot) => {
         if (querySnapshot.empty) {
           setConversations([]);
           setActiveConversations([]);
           return;
         }

         const chatPromises = [];
         const contactIds = [];
         
         querySnapshot.forEach((chatDoc) => {
           const contactId = chatDoc.id;
           const chatData = chatDoc.data();
           
           if (contactId && chatData) {
             contactIds.push(contactId);
             chatPromises.push({
               contactId,
               chatData,
               promise: getDoc(doc(db, "users", contactId))
             });
           }
         });

         // Process promises in smaller batches to avoid overwhelming Firestore
         const batchSize = 5;
         const resolvedChats = [];
         
         for (let i = 0; i < chatPromises.length; i += batchSize) {
           const batch = chatPromises.slice(i, i + batchSize);
           const batchResults = await Promise.allSettled(
             batch.map(async ({ contactId, chatData, promise }) => {
               try {
                 const userDoc = await promise;
                 if (userDoc.exists()) {
                   const contactData = userDoc.data();
                   const displayName = contactData.username || contactData.email?.split('@')[0] || 'User';
                   return {
                     id: contactId,
                     ...contactData,
                     displayName,
                     lastMessage: chatData.lastMessage,
                     lastMessageTime: chatData.lastMessageTime,
                     unread: chatData.unread || false
                   };
                 }
                 return null;
               } catch (error) {
                 console.error(`Error fetching contact ${contactId}:`, error);
                 return null;
               }
             })
           );
           
           batchResults.forEach(result => {
             if (result.status === 'fulfilled' && result.value) {
               resolvedChats.push(result.value);
             }
           });
         }

         // Sort by lastMessageTime
         const sortedChats = resolvedChats.sort((a, b) => {
           if (!a.lastMessageTime && !b.lastMessageTime) return 0;
           if (!a.lastMessageTime) return 1;
           if (!b.lastMessageTime) return -1;
           
           const timeA = a.lastMessageTime.seconds || 0;
           const timeB = b.lastMessageTime.seconds || 0;
           return timeB - timeA;
         });
         
         setConversations(sortedChats);
         const activeChats = sortedChats.filter(chat => 
           chat.lastMessage !== undefined && chat.lastMessage !== null
         );
         setActiveConversations(activeChats);
       }, (error) => {
         console.error("Error fetching chats:", error);
       });

     } catch (error) {
       console.error("Error setting up listeners:", error);
     }
   };

   setupRealtimeListeners();

   // Cleanup function
   return () => {
     usersUnsubscribe();
     chatsUnsubscribe();
     
     // Update user status on cleanup
     if (user?.uid) {
       setDoc(doc(db, "users", user.uid), {
         isOnline: false,
         lastSeen: serverTimestamp()
       }, { merge: true }).catch(console.error);
     }
   };
 }, [user]);

 // Handle search when explicitly triggered
 const performSearch = () => {
   if (!hasSearchAccess) return;
   
   if (searchQuery.trim() === '') {
     setSearchResults([]);
     return;
   }
  
   const results = allContacts.filter((contact) => {
     const searchField = contact.username || contact.displayName || '';
     return searchField.toLowerCase() === searchQuery.toLowerCase();
   });
  
   setSearchResults(results);
 };

 // Clear search
 const clearSearch = () => {
   if (!hasSearchAccess) return;
   
   setSearchQuery('');
   setSearchResults([]);
 };

 // Optimized message loading with notification detection
 useEffect(() => {
   if (!selectedContact?.id || !user?.uid) {
     setMessages([]);
     lastMessageCountRef.current = 0;
     return;
   }
  
   setIsLoading(true);
   const chatId = getChatId(user.uid, selectedContact.id);
  
   let unsubscribe = () => {};

   const setupMessageListener = async () => {
     try {
       const chatDocRef = doc(db, "chats", chatId);
       
       // Check/create chat document without blocking
       getDoc(chatDocRef).then(docSnap => {
         if (!docSnap.exists()) {
           setDoc(chatDocRef, {
             users: [user.uid, selectedContact.id],
             createdAt: serverTimestamp(),
             updatedAt: serverTimestamp()
           }).catch(console.error);
         }
       }).catch(console.error);
      
       const messagesRef = collection(db, "chats", chatId, "messages");
       const q = query(messagesRef, orderBy("timestamp", "asc"));
      
       unsubscribe = onSnapshot(q, (querySnapshot) => {
         const messagesList = [];
         querySnapshot.forEach((docSnapshot) => {
           const messageData = docSnapshot.data();
           if (messageData && docSnapshot.id) {
             messagesList.push({
               id: docSnapshot.id,
               text: messageData.text || '',
               imageUrl: messageData.imageUrl || null,
               senderId: messageData.senderId,
               senderName: messageData.senderName,
               timestamp: messageData.timestamp
             });
           }
         });
        
         const finalMessages = messagesList.filter(m => !m.id.startsWith('temp-'));
         
         // Check for new messages and show notification
         if (finalMessages.length > lastMessageCountRef.current && lastMessageCountRef.current > 0) {
           const newMessages = finalMessages.slice(lastMessageCountRef.current);
           const latestMessage = newMessages[newMessages.length - 1];
           
           // Only show notification if the message is from someone else
           if (latestMessage && latestMessage.senderId !== user.uid) {
             const messagePreview = latestMessage.text || (latestMessage.imageUrl ? 'Sent an image' : 'New message');
             showNotification(`${latestMessage.senderName}: ${messagePreview}`);
           }
         }
         
         lastMessageCountRef.current = finalMessages.length;
         setMessages(finalMessages);
         setIsLoading(false);
        
         // Mark as read in background
         if (selectedContact && finalMessages.length > 0) {
           const userChatRef = doc(db, "users", user.uid, "chats", selectedContact.id);
           setDoc(userChatRef, { unread: false }, { merge: true }).catch(console.error);
         }
       }, (error) => {
         console.error("Error loading messages:", error);
         setIsLoading(false);
       });
     } catch (error) {
       console.error("Error in message setup:", error);
       setIsLoading(false);
     }
   };

   setupMessageListener();
   return unsubscribe;
 }, [selectedContact, user]);

 // Scroll to bottom when messages change
 useEffect(() => {
   if (messages.length > 0) {
     messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
   }
 }, [messages]);

 // Generate a consistent chat ID for two users
 const getChatId = (uid1, uid2) => {
   return uid1 < uid2 ? `${uid1}_${uid2}` : `${uid2}_${uid1}`;
 };

 // Upload image to Cloudinary
 const uploadToCloudinary = async (file) => {
   const formData = new FormData();
   formData.append('file', file);
   formData.append('upload_preset', cloudinaryConfig.uploadPreset);
   formData.append('folder', `chat-app/${getChatId(user.uid, selectedContact.id)}`);
   
   try {
     const xhr = new XMLHttpRequest();
     
     xhr.upload.onprogress = (event) => {
       if (event.lengthComputable) {
         const percentComplete = Math.round((event.loaded / event.total) * 100);
         setUploadProgress(percentComplete);
       }
     };
     
     return new Promise((resolve, reject) => {
       xhr.open('POST', cloudinaryConfig.apiUrl, true);
       
       xhr.onload = function() {
         if (xhr.status === 200) {
           const response = JSON.parse(xhr.responseText);
           resolve(response.secure_url);
         } else {
           reject(new Error('Upload failed'));
         }
       };
       
       xhr.onerror = function() {
         reject(new Error('Upload failed'));
       };
       
       xhr.send(formData);
     });
   } catch (error) {
     console.error('Error uploading to Cloudinary:', error);
     throw error;
   }
 };

 // Send email notification to offline recipient
 const sendEmailNotification = async (recipientData, messageContent) => {
   try {
     if (!recipientData.isOnline && recipientData.email) {
       const emailParams = {
         to_email: recipientData.email,
         to_name: recipientData.username || recipientData.displayName,
         from_name: user.username || user.displayName,
         message: messageContent,
         app_url: window.location.origin
       };
       
       console.log("Sending email notification to offline user:", recipientData.email);
       
       await emailjs.send(
         emailjsConfig.serviceId,
         emailjsConfig.templateId,
         emailParams
       );
       
       console.log("Email notification sent successfully");
     }
   } catch (error) {
     console.error("Error sending email notification:", error);
   }
 };

 const handleSendMessage = async () => {
   if ((!messageText.trim() && !imageFile) || !selectedContact) return;

   const textToSend = messageText.trim();
  
   setMessageText('');
   setImageFile(null);
   setImagePreview(null);
   setUploadProgress(0);
  
   try {
     const chatId = getChatId(user.uid, selectedContact.id);
    
     const tempMessageId = `temp-${Date.now()}`;
    
     const optimisticMessage = {
       id: tempMessageId,
       text: textToSend,
       senderId: user.uid,
       senderName: user.username || user.displayName || user.email.split('@')[0],
       timestamp: { seconds: Math.floor(Date.now() / 1000) }
     };
    
     if (imageFile) {
       optimisticMessage.imageLoading = true;
     }
    
     setMessages(prev => [...prev, optimisticMessage]);
    
     await setDoc(doc(db, "chats", chatId), {
       users: [user.uid, selectedContact.id],
       updatedAt: serverTimestamp()
     }, { merge: true });
    
     let imageUrl = null;
    
     if (imageFile) {
       imageUrl = await uploadToCloudinary(imageFile);
     }
    
     const senderName = user.username || user.displayName || user.email.split('@')[0];
    
     const messageRef = await addDoc(collection(db, "chats", chatId, "messages"), {
       text: textToSend,
       imageUrl,
       senderId: user.uid,
       senderName: senderName,
       timestamp: serverTimestamp()
     });
    
     const userChatRef = doc(db, "users", user.uid, "chats", selectedContact.id);
     const recipientChatRef = doc(db, "users", selectedContact.id, "chats", user.uid);
    
     const chatData = {
       lastMessage: textToSend || (imageUrl ? 'Sent an image' : ''),
       lastMessageTime: serverTimestamp(),
       unread: false
     };
    
     await setDoc(userChatRef, chatData, { merge: true });
     await setDoc(recipientChatRef, { ...chatData, unread: true }, { merge: true });
     
     const recipientDoc = await getDoc(doc(db, "users", selectedContact.id));
     if (recipientDoc.exists()) {
       const recipientData = recipientDoc.data();
       
       if (!recipientData.isOnline) {
         const notificationContent = textToSend || (imageUrl ? 'Sent you an image' : '');
         await sendEmailNotification(recipientData, notificationContent);
       }
     }
    
   } catch (error) {
     console.error("Error sending message:", error);
     setMessages(prev => prev.filter(msg => !msg.id.startsWith('temp-')));
     alert("Failed to send message. Please try again.");
   }
 };

 const handleFileChange = (e) => {
   if (e.target.files[0]) {
     const file = e.target.files[0];
     
     if (file.size > 10 * 1024 * 1024) {
       alert("File size exceeds 10MB. Please select a smaller image.");
       return;
     }
     
     setImageFile(file);
    
     const reader = new FileReader();
     reader.onloadend = () => {
       setImagePreview(reader.result);
     };
     reader.readAsDataURL(file);
   }
 };

 const handleLogout = async () => {
   try {
     if (user && user.uid) {
       await setDoc(doc(db, "users", user.uid), {
         isOnline: false,
         lastSeen: serverTimestamp()
       }, { merge: true });
     }
    
     await signOut(auth);
   } catch (error) {
     console.error("Error logging out:", error);
   }
 };

 const formatTimestamp = (timestamp) => {
   if (!timestamp) return '';
  
   const date = new Date(timestamp.seconds * 1000);
   const now = new Date();
  
   if (date.toDateString() === now.toDateString()) {
     return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
   }
  
   if (date.getFullYear() === now.getFullYear()) {
     return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
   }
  
   return date.toLocaleDateString();
 };

 const toggleSidebar = () => {
   setShowSidebar(!showSidebar);
 };

 // Handle key press for textarea (Enter for new line, Send button for sending)
 const handleKeyDown = (e) => {
   // Allow Enter to create new lines, only prevent default if we're sending via button
   if (e.key === 'Enter' && !e.shiftKey) {
     // Don't prevent default - let Enter create new line
     return;
   }
 };

 return (
   <div className="flex flex-col h-screen bg-stone-50">
     {/* Notification Banner */}
     {notification && (
       <NotificationBanner
         message={notification}
         onClose={() => setNotification(null)}
       />
     )}

     {/* Modern Header */}
     <div className="bg-gradient-to-r from-stone-800 to-stone-700 text-white shadow-lg border-b border-stone-600">
       <div className="flex items-center justify-between p-4">
         <div className="flex items-center">
           <button 
             className="md:hidden mr-3 p-2 rounded-lg hover:bg-stone-600 transition-colors" 
             onClick={toggleSidebar}
           >
             <Menu size={20} />
           </button>
           <div className="flex items-center bg-stone-600/50 rounded-full p-1 pr-4">
             <div className="rounded-full p-2 mr-3 bg-stone-500">
               {user.profilePic ? (
                 <img
                   src={user.profilePic}
                   alt={user.username || user.displayName}
                   className="w-8 h-8 rounded-full object-cover"
                 />
               ) : (
                 <User size={20} className="text-stone-100" />
               )}
             </div>
             <div>
               <h3 className="font-semibold text-stone-100">{user.username || user.displayName}</h3>
               <p className="text-xs text-stone-300">Online</p>
             </div>
           </div>
         </div>
         
         <div className="flex items-center space-x-2">
           <Link
             to="/"
             className="text-stone-200 hover:text-white flex items-center px-4 py-2 rounded-lg hover:bg-stone-600 transition-colors"
           >
             <ArrowRightToLine size={16} className="mr-2" />
             <span className="hidden sm:inline">Home</span>
           </Link>
         </div>
       </div>
       
       {/* Enhanced Search Bar - Only show if user has access */}
       {hasSearchAccess && (
         <div className="px-4 pb-4">
           <div className="flex items-center bg-stone-600/30 rounded-lg border border-stone-500">
             <div className="relative flex-1">
               <input
                 type="text"
                 placeholder="Search users by exact username..."
                 value={searchQuery}
                 onChange={(e) => setSearchQuery(e.target.value)}
                 onKeyPress={(e) => {
                   if (e.key === 'Enter') {
                     performSearch();
                   }
                 }}
                 className="w-full p-3 pl-10 bg-transparent text-white placeholder-stone-300 focus:outline-none"
               />
               <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-300" />
               
               {searchQuery && (
                 <button
                   onClick={clearSearch}
                   className="absolute right-3 top-1/2 transform -translate-y-1/2 text-stone-300 hover:text-white transition-colors"
                 >
                   <X size={16} />
                 </button>
               )}
             </div>
             <button
               onClick={performSearch}
               className="p-3 bg-stone-700 text-white rounded-r-lg hover:bg-stone-600 transition-colors focus:outline-none border-l border-stone-500"
             >
               <Search size={18} />
             </button>
           </div>
         </div>
       )}
     </div>

     <div className="flex flex-1 overflow-hidden">
       {/* Modern Sidebar */}
       <div className={`${showSidebar ? 'block' : 'hidden'} md:block w-full md:w-80 bg-white border-r border-stone-200 flex flex-col shadow-sm`}>
         <div className="p-4 border-b border-stone-200 bg-stone-50">
           <h2 className="text-xl font-bold text-stone-800 flex items-center">
             <Users className="mr-3 text-stone-600" size={24} />
             Messages
           </h2>
         </div>
         
         <div className="overflow-y-auto flex-1">
           {/* Search Results - Only show if user has access */}
           {hasSearchAccess && searchResults.length > 0 && (
             <div className="border-b border-stone-200 bg-stone-25">
               <div className="px-4 py-3 bg-stone-100">
                 <h3 className="text-sm font-semibold text-stone-600 uppercase tracking-wide">Search Results</h3>
               </div>
               {searchResults.map((contact) => (
                 <div
                   key={contact.id}
                   className={`p-4 cursor-pointer hover:bg-stone-50 transition-colors border-l-4 ${
                     selectedContact?.id === contact.id ? 'bg-stone-100 border-l-stone-600' : 'border-l-transparent'
                   }`}
                   onClick={() => {
                     setSelectedContact(contact);
                     if (window.innerWidth < 768) {
                       setShowSidebar(false);
                     }
                   }}
                 >
                   <div className="flex items-center">
                     <div className="relative">
                       <div className={`rounded-full p-2 mr-3 ${contact.isOnline ? 'bg-green-100' : 'bg-stone-100'}`}>
                         {contact.profilePic ? (
                           <img
                             src={contact.profilePic}
                             alt={contact.username || contact.displayName}
                             className="w-10 h-10 rounded-full object-cover"
                           />
                         ) : (
                           <User size={20} className="text-stone-600" />
                         )}
                       </div>
                       {contact.isOnline && (
                         <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
                       )}
                     </div>
                     <div className="flex-1 min-w-0">
                       <h3 className="font-semibold text-stone-800 truncate">{contact.username || contact.displayName}</h3>
                       <p className="text-sm text-stone-500">
                         {contact.isOnline ? 'Online' : 'Offline'}
                       </p>
                     </div>
                   </div>
                 </div>
               ))}
             </div>
           )}

           {/* Recent Conversations */}
           <div>
             <div className="px-4 py-3 bg-stone-100">
               <h3 className="text-sm font-semibold text-stone-600 uppercase tracking-wide">Recent Chats</h3>
             </div>
             {activeConversations.length > 0 ? (
               activeConversations.map((contact) => (
                 <div
                   key={contact.id}
                   className={`p-4 cursor-pointer hover:bg-stone-50 transition-colors border-l-4 ${
                     selectedContact?.id === contact.id ? 'bg-stone-100 border-l-stone-600' : 'border-l-transparent'
                   }`}
                   onClick={() => {
                     setSelectedContact(contact);
                     if (window.innerWidth < 768) {
                       setShowSidebar(false);
                     }
                   }}
                 >
                   <div className="flex items-center">
                     <div className="relative">
                       <div className={`rounded-full p-2 mr-3 ${contact.isOnline ? 'bg-green-100' : 'bg-stone-100'}`}>
                         {contact.profilePic ? (
                           <img
                             src={contact.profilePic}
                             alt={contact.username || contact.displayName}
                             className="w-10 h-10 rounded-full object-cover"
                           />
                         ) : (
                           <User size={20} className="text-stone-600" />
                         )}
                       </div>
                       {contact.isOnline && (
                         <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
                       )}
                     </div>
                     <div className="flex-1 min-w-0">
                       <div className="flex justify-between items-start">
                         <h3 className="font-semibold text-stone-800 truncate">{contact.username || contact.displayName}</h3>
                         {contact.lastMessageTime && (
                           <span className="text-xs text-stone-500 ml-2 flex-shrink-0">
                             {formatTimestamp(contact.lastMessageTime)}
                           </span>
                         )}
                       </div>
                       <div className="flex items-center justify-between">
                         <p className="text-sm text-stone-600 truncate max-w-48">{contact.lastMessage}</p>
                         {contact.unread && (
                           <span className="w-2 h-2 bg-stone-600 rounded-full ml-2 flex-shrink-0"></span>
                         )}
                       </div>
                     </div>
                   </div>
                 </div>
               ))
             ) : (
               <div className="p-8 text-center">
                 <Users size={48} className="text-stone-300 mb-4 mx-auto" />
                 <p className="text-stone-500 font-medium">No conversations yet</p>
                 {hasSearchAccess ? (
                   <p className="text-stone-400 text-sm mt-1">Search for users to start chatting</p>
                 ) : (
                   <p className="text-stone-400 text-sm mt-1">Wait for users to message you</p>
                 )}
               </div>
             )}
           </div>
         </div>
       </div>

       {/* Modern Chat Area */}
       <div className={`${showSidebar && window.innerWidth < 768 ? 'hidden' : 'flex'} flex-col flex-1 bg-stone-25`}>
         {selectedContact ? (
           <>
             {/* Chat Header */}
             <div className="p-4 border-b border-stone-200 bg-white shadow-sm">
               <div className="flex items-center justify-between">
                 <div className="flex items-center">
                   <button
                     className="md:hidden mr-3 p-2 rounded-lg hover:bg-stone-100 transition-colors"
                     onClick={toggleSidebar}
                   >
                     <ArrowLeft size={20} />
                   </button>
                   <div className="relative">
                     <div className={`rounded-full p-2 mr-3 ${selectedContact.isOnline ? 'bg-green-100' : 'bg-stone-100'}`}>
                       {selectedContact.profilePic ? (
                         <img
                           src={selectedContact.profilePic}
                           alt={selectedContact.username || selectedContact.displayName}
                           className="w-10 h-10 rounded-full object-cover"
                         />
                       ) : (
                         <User size={20} className="text-stone-600" />
                       )}
                     </div>
                     {selectedContact.isOnline && (
                       <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
                     )}
                   </div>
                   <div>
                     <h3 className="font-bold text-stone-800">{selectedContact.username || selectedContact.displayName}</h3>
                     <p className="text-sm text-stone-500">
                       {selectedContact.isOnline ? 'Online' : `Last seen ${formatTimestamp(selectedContact.lastSeen)}`}
                     </p>
                   </div>
                 </div>
          {/* Session time notice */}
           <div className="hidden sm:block bg-stone-400 text-stone-200 px-3 py-2 rounded-lg text-sm">
             Each session can only last for up to 20 mins.
           </div>
                 <div className="flex items-center space-x-2">
                   {/* Mobile session notice */}
                   <div className="sm:hidden bg-stone-400 text-stone-600 px-2 py-1 rounded text-xs">
                   Each session can only last for up to 20 mins.
                   </div>
                  
                 </div>
               </div>
             </div>

             {/* Messages Area */}
             <div className="flex-1 overflow-y-auto p-4 bg-gradient-to-b from-stone-25 to-stone-50">
               {isLoading ? (
                 <div className="flex items-center justify-center h-full">
                   <div className="text-center">
                     <div className="w-8 h-8 border-3 border-stone-300 border-t-stone-600 rounded-full animate-spin mb-4 mx-auto"></div>
                     <p className="text-stone-600">Loading messages...</p>
                   </div>
                 </div>
               ) : messages.length === 0 ? (
                 <div className="flex items-center justify-center h-full">
                   <div className="text-center">
                     <div className="w-16 h-16 bg-stone-200 rounded-full flex items-center justify-center mb-4 mx-auto">
                       <Users size={32} className="text-stone-400" />
                     </div>
                     <p className="text-stone-600 font-medium">No messages yet</p>
                     <p className="text-stone-500 text-sm">Start the conversation by saying hello!</p>
                   </div>
                 </div>
               ) : (
                 <div className="space-y-4">
                   {messages.map((message) => (
                     <div
                       key={message.id}
                       className={`flex ${message.senderId === user.uid ? 'justify-end' : 'justify-start'}`}
                     >
                       <div
                         className={`max-w-xs md:max-w-md rounded-2xl p-4 shadow-sm ${
                           message.senderId === user.uid
                             ? 'bg-stone-700 text-white rounded-br-md'
                             : 'bg-white text-stone-800 rounded-bl-md border border-stone-200'
                         }`}
                       >
                         {message.text && (
                           <p className="break-words leading-relaxed whitespace-pre-wrap">{message.text}</p>
                         )}
                         
                         {message.imageLoading && (
                           <div className="mt-3 bg-stone-100 h-40 w-full rounded-lg flex items-center justify-center">
                             <div className="text-center">
                               <div className="w-6 h-6 border-2 border-stone-400 border-t-stone-600 rounded-full animate-spin mb-2 mx-auto"></div>
                               <p className="text-stone-600 text-sm">Uploading {uploadProgress}%</p>
                             </div>
                           </div>
                         )}
                         
                         {message.imageUrl && (
                           <div className="mt-3">
                             <img
                               src={message.imageUrl}
                               alt="Shared image"
                               className="max-w-full rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                               onClick={() => window.open(message.imageUrl, '_blank')}
                             />
                           </div>
                         )}
                         
                         <div className={`text-xs mt-2 ${
                           message.senderId === user.uid ? 'text-stone-300' : 'text-stone-500'
                         }`}>
                           {formatTimestamp(message.timestamp)}
                         </div>
                       </div>
                     </div>
                   ))}
                   <div ref={messagesEndRef} />
                 </div>
               )}
             </div>

             {/* Modern Message Input */}
             <div className="p-4 bg-white border-t border-stone-200">
               {/* Donation Button */}
               <div className="px-4 py-2">
                <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-lg p-3 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Heart className="text-red-500 mr-2 flex-shrink-0" size={16} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-red-800">Help keep this service free</p>
                      </div>
                    </div>
                    <Link to="https://www.paypal.com/ncp/payment/JZUGEFACMSY8Y"
                      
                      className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm flex items-center ml-3 flex-shrink-0"
                    >
                      <Coffee className="mr-1" size={14} />
                      Donate
                    </Link>
                  </div>
                </div>
              </div>

               {imagePreview && (
                 <div className="mb-3 relative inline-block">
                   <img src={imagePreview} alt="Preview" className="h-20 rounded-lg shadow-sm" />
                   <button
                     className="absolute -top-2 -right-2 bg-stone-600 rounded-full p-1 text-white hover:bg-stone-700 transition-colors"
                     onClick={() => {
                       setImageFile(null);
                       setImagePreview(null);
                     }}
                   >
                     <X size={14} />
                   </button>
                 </div>
               )}
               
               <div className="flex items-start bg-stone-50 rounded-2xl border border-stone-200">
                 <button
                   onClick={() => fileInputRef.current?.click()}
                   className="p-3 text-stone-500 hover:text-stone-700 hover:bg-stone-100 rounded-full transition-colors ml-2 mt-1"
                 >
                   <Image size={20} />
                 </button>
                 <input
                   type="file"
                   ref={fileInputRef}
                   onChange={handleFileChange}
                   accept="image/*"
                   className="hidden"
                 />
                 <textarea
                   placeholder="Type a message..."
                   value={messageText}
                   onChange={(e) => setMessageText(e.target.value)}
                   onKeyDown={handleKeyDown}
                   className="flex-1 p-3 bg-transparent focus:outline-none text-stone-800 placeholder-stone-500 resize-none min-h-12 max-h-32"
                   rows={1}
                   style={{ 
                     height: 'auto',
                     minHeight: '48px'
                   }}
                   onInput={(e) => {
                     e.target.style.height = 'auto';
                     e.target.style.height = Math.min(e.target.scrollHeight, 128) + 'px';
                   }}
                 />
                 <button
                   onClick={handleSendMessage}
                   disabled={!messageText.trim() && !imageFile}
                   className={`p-3 rounded-full mr-2 mt-1 transition-all ${
                     !messageText.trim() && !imageFile
                       ? 'bg-stone-200 text-stone-400'
                       : 'bg-stone-700 text-white hover:bg-stone-800 shadow-sm'
                   }`}
                 >
                   <Send size={18} />
                 </button>
               </div>
             </div>
           </>
         ) : (
           <div className="flex flex-col items-center justify-center h-full bg-gradient-to-b from-stone-25 to-stone-50">
             <div className="text-center max-w-md">
               <div className="w-24 h-24 bg-stone-200 rounded-full flex items-center justify-center mb-6 mx-auto">
                 <Users size={48} className="text-stone-400" />
               </div>
               <h2 className="text-2xl font-bold text-stone-700 mb-3">Welcome to Messages</h2>
               <p className="text-stone-500 mb-6">
                 {activeConversations.length > 0 
                   ? "Select a conversation to continue chatting" + (hasSearchAccess ? " or search for new users" : "")
                   : hasSearchAccess 
                     ? "Search for users above to start your first conversation"
                     : "Wait for users to message you to start conversations"}
               </p>
               <button
                 onClick={toggleSidebar}
                 className="md:hidden px-6 py-3 bg-stone-700 text-white rounded-full hover:bg-stone-800 transition-colors shadow-sm"
               >
                 <Users className="inline mr-2" size={18} />
                 Browse Contacts
               </button>
             </div>
           </div>
         )}
       </div>
     </div>

     <style jsx>{`
       @keyframes slide-down {
         from {
           transform: translateY(-100%);
           opacity: 0;
         }
         to {
           transform: translateY(0);
           opacity: 1;
         }
       }
       
       .animate-slide-down {
         animation: slide-down 0.3s ease-out;
       }
     `}</style>
   </div> 
 );
}

export default Chat;